import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SOCKET_ID_VERSION_CONFIG_KEY } from "../../utils/dynamicSockets/dynamicSocketId";
import { SystemEntity, CLASSIFIABLE_EXTENSIONS } from "@erp/shared-types";

/**
 * An inline category definition for classification.
 */
const inlineCategorySchema = z.object({
  name: z.string().describe("Category name"),
  description: z
    .string()
    .describe(
      "Description to help the AI understand when to apply this category",
    ),
});

/**
 * Output keys reserved by the step itself; user-named categories cannot
 * collide with these. The parallel-array sockets are the original
 * classify_email contract — existing graphs wire `categoryName` into a
 * decision node and must keep resolving.
 */
export const CLASSIFY_EMAIL_RESERVED_SOCKET_IDS = [
  "categoryId",
  "categoryName",
  "confidence",
  "source",
  "document",
  "documentName",
  "reasoning",
] as const;

/**
 * CoreEnum options snapshotted into config so the sync socket generator can
 * emit per-category sockets without a DB lookup. Runtime classification still
 * loads the live enum.
 */
export const CLASSIFY_EMAIL_ENUM_CATEGORIES_CONFIG_KEY = "__enumCategories";

/**
 * The original parallel-array output shape. Kept as its own object schema so
 * the dynamic generator can re-emit those sockets with the same ids/types
 * the pre-dynamic node had, while `outputSchema` is a record so per-category
 * keys survive `executeStep` validation.
 */
export const classifyEmailReservedOutputSchema = z.object({
  // Parallel arrays — one entry per classified item (attachment or body).
  // Connect individual sockets to downstream steps; fanout triggers
  // automatically when wired to a single-value input.
  categoryId: z
    .array(z.string())
    .describe("Classification ID per item (enum option ID or slugified name)"),
  categoryName: z
    .array(z.string())
    .describe("Human-readable category name per item"),
  confidence: z
    .array(z.number())
    .describe("Classification confidence score (0-1) per item"),
  source: z
    .array(z.string())
    .describe('Source per item: "attachment" or "body"'),
  document: z
    .array(entityOfType(SystemEntity.CORE_DOCUMENT).nullable())
    .describe(
      "Document entity reference per item (null for body classifications)",
    ),
  documentName: z
    .array(z.string().nullable())
    .describe("Original filename per item (null for body classifications)"),
  reasoning: z.array(z.string()).describe("Extend AI reasoning per item"),
});

/**
 * Classify Email Step
 *
 * Two classification modes:
 * - **per_item**: Classifies individual attachments and/or body text separately.
 *   Returns one result per item, suitable for fanout.
 * - **entire_email**: Classifies the entire email as a single entity using
 *   subject, body text, and attachment filenames as context. Returns one result.
 *
 * Categories can come from either:
 * - A CoreEnum (each option becomes a classification category)
 * - An inline list of name + description pairs
 *
 * Outputs always include the reserved parallel arrays above, plus one
 * `array<json>` socket per configured category so downstream steps can
 * branch on type without a decision node. See
 * `generateClassifyEmailOutputSockets`.
 */
export const classifyEmailStep = defineStepConfig({
  type: "classify_email",
  name: "Classify Email",
  description:
    "Classify email content into configurable categories — per attachment/body or as a whole email",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to classify",
    ),
  }),

  // Dynamic per-category keys must survive Zod validation. The reserved
  // parallel-array sockets are re-emitted by the generator from
  // `classifyEmailReservedOutputSchema`.
  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: z
    .object({
      classificationMode: z
        .enum(["per_item", "entire_email"])
        .optional()
        .default("per_item")
        .describe(
          "per_item: classify each attachment/body separately. entire_email: classify the whole email as one entity.",
        ),
      classificationSource: z
        .enum(["enum", "inline"])
        .default("inline")
        .describe(
          'Where categories come from: "enum" for CoreEnum, "inline" for direct definition',
        ),
      coreEnumId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "CoreEnum ID to load categories from (when classificationSource is enum)",
        ),
      categories: z
        .array(inlineCategorySchema)
        .optional()
        .describe(
          "Inline category definitions (when classificationSource is inline)",
        ),
      [CLASSIFY_EMAIL_ENUM_CATEGORIES_CONFIG_KEY]: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
          }),
        )
        .optional()
        .describe(
          "Snapshot of CoreEnum options used to generate per-category output sockets. Written by the editor when an enum is selected; not used at classification time.",
        ),
      // --- per_item mode options ---
      classifyAttachments: z
        .boolean()
        .optional()
        .default(true)
        .describe("Whether to classify file attachments (per_item mode only)"),
      allowedFileExtensions: z
        .array(
          z.enum(CLASSIFIABLE_EXTENSIONS as unknown as [string, ...string[]]),
        )
        .optional()
        .describe(
          "File extensions to classify (per_item mode only, required when classifyAttachments is true).",
        ),
      classifyBody: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          "Whether to classify the email body text (per_item mode only)",
        ),
      useEmailContext: z
        .boolean()
        .optional()
        .default(true)
        .describe(
          "Prepend the email subject and body preview as context when classifying attachments (per_item mode only)",
        ),
      // --- shared options ---
      confidenceThreshold: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .default(0.5)
        .describe("Minimum confidence score to include in results"),
      includeOther: z
        .boolean()
        .optional()
        .default(true)
        .describe('Whether to include an "other" fallback classification'),
      classificationRules: z
        .string()
        .optional()
        .describe("Natural language guidance for classification"),
      baseProcessor: z
        .enum(["classification_performance", "classification_light"])
        .optional()
        .describe("Extend AI classification model to use"),
      // Declared so z.object() does not strip the stamp on parse. `executeStep`
      // parses the persisted config through this schema before handing it to the
      // handler, and the handler derives per-category payload keys from the
      // version: drop the stamp and it writes bare ids for sockets the canvas
      // marked `::dynamic`.
      [SOCKET_ID_VERSION_CONFIG_KEY]: z
        .number()
        .int()
        .optional()
        .describe(
          "Socket-id scheme this step was created under. Absent means the legacy scheme, where a category's slug IS its output socket id.",
        ),
    })
    .refine(
      (data) => {
        if (
          (data.classificationMode ?? "per_item") === "per_item" &&
          (data.classifyAttachments ?? true)
        ) {
          return (
            Array.isArray(data.allowedFileExtensions) &&
            data.allowedFileExtensions.length > 0
          );
        }
        return true;
      },
      {
        message:
          "At least one file type must be selected when classifying attachments",
        path: ["allowedFileExtensions"],
      },
    ),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
    nonRetryableErrorTypes: [
      "WorkflowGuardrailBlockedError",
      "WorkflowGuardrailReviewRequiredError",
    ],
  },

  evalConfig: {
    evalOutputKeys: ["categoryId"],
    outputFormat: "parallel_arrays",
    contextOutputKeys: ["categoryName", "document", "documentName", "source"],
  },
});
